/**
 * editorSeedCache
 *
 * Module-level cache for editor-init seed data, shared across all
 * EditorProviders mounts within a single SPA session.
 *
 * Extracted from EditorProviders.tsx so that other providers (e.g.
 * QuestionOptionsProvider) can keep the cached seed in sync with their
 * own in-memory mutations without creating a circular import on
 * EditorProviders.
 *
 * Why this matters:
 *   EditorProviders remounts on every route navigation. It seeds child
 *   providers from this cache instead of re-fetching editor-init. If a
 *   provider mutates state but the cache is not updated, navigating away
 *   and back will rehydrate from a stale cache and visually "lose" the
 *   user's edits until a full page refresh re-fetches editor-init.
 */

import { fetchEditorInit } from '@/services/api';
import { sanitizeQuestionOptionsState } from '@/services/questionOptionsService';
import {
  type QuestionOptionsStateRecord,
} from '@/types';

export interface EditorSeed {
  questionOptionsSeed: QuestionOptionsStateRecord | undefined;
  ready: boolean;
  ownerUserId: number | undefined;
  preferencesSeed: unknown;
}

let cachedEditorSeed: EditorSeed | null = null;
let editorInitPromise: Promise<EditorSeed> | null = null;
let editorInitPromiseUserId: number | undefined;

export function getCachedEditorSeed(
  userId: number | undefined
): EditorSeed | undefined {
  if (cachedEditorSeed && cachedEditorSeed.ownerUserId === userId) {
    return cachedEditorSeed;
  }
  return undefined;
}

export function setCachedEditorSeed(seed: EditorSeed): void {
  cachedEditorSeed = seed;
}

/**
 * Start (or join) the editor-init request for a user.
 *
 * Keeping both the fetch and its in-flight promise here lets LayoutBootstrap
 * prefetch the same request that EditorProviders later consumes, without
 * introducing duplicate network requests.
 */
export function prefetchEditorInit(
  userId: number | undefined
): Promise<EditorSeed> {
  const cachedSeed = getCachedEditorSeed(userId);
  if (cachedSeed) return Promise.resolve(cachedSeed);

  if (editorInitPromise && editorInitPromiseUserId === userId) {
    return editorInitPromise;
  }

  if (!userId) {
    const seed: EditorSeed = {
      questionOptionsSeed: undefined,
      ready: true,
      ownerUserId: userId,
      preferencesSeed: undefined,
    };
    cachedEditorSeed = seed;
    return Promise.resolve(seed);
  }

  const request: Promise<EditorSeed> = fetchEditorInit()
    .then((data) => ({
      questionOptionsSeed: sanitizeQuestionOptionsState(data.question_options as QuestionOptionsStateRecord),
      ready: true,
      ownerUserId: userId,
      preferencesSeed: data.preferences,
    }))
    .catch(() => ({
      questionOptionsSeed: undefined,
      ready: true,
      ownerUserId: userId,
      preferencesSeed: undefined,
    }))
    .then((seed) => {
      // A previous user's request may settle after a user transition. Do not
      // let that stale request overwrite the current user's cache/promise.
      if (editorInitPromise === request && editorInitPromiseUserId === userId) {
        cachedEditorSeed = seed;
        editorInitPromise = null;
        editorInitPromiseUserId = undefined;
      }
      return seed;
    });

  editorInitPromise = request;
  editorInitPromiseUserId = userId;
  return request;
}

/**
 * Sync the cached question options seed with the latest in-memory state.
 *
 * Called by QuestionOptionsProvider whenever its state changes, so that a
 * subsequent EditorProviders remount (triggered by route navigation) can
 * rehydrate child providers from the up-to-date snapshot instead of a
 * stale editor-init response.
 */
export function updateCachedQuestionOptionsSeed(
  userId: number | undefined,
  qoState: QuestionOptionsStateRecord
): void {
  if (!cachedEditorSeed) return;
  if (cachedEditorSeed.ownerUserId !== userId) return;
  cachedEditorSeed = {
    ...cachedEditorSeed,
    questionOptionsSeed: qoState,
  };
}

export function clearEditorSeedCache(): void {
  cachedEditorSeed = null;
  editorInitPromise = null;
  editorInitPromiseUserId = undefined;
}
